import { I18n } from "@assets/strings";
import React, { useMemo } from "react";
import styled from "styled-components";
import { useCurrentProfile } from "@domain/currentProfile/hooks";
import { ArticleDraft } from "@domain/lms/article/article";
import { Author } from "@domain/lms/content";
import { CourseDraft } from "@domain/lms/course/courseDraft";
import { QuizDraft } from "@domain/lms/quiz/quizDraft";
import { useMyOrganizations } from "@domain/organization/hooks";
import { Person } from "@domain/user/user";
import { Primary } from "@ui/components/containers/primary";
import { PersonSelector } from "@ui/components/inputs/personSelector";
import { RadioButton } from "@ui/components/inputs/radio";
import { stack } from "@ui/components/layout";

interface PublisherSelectorProps extends React.HTMLAttributes<HTMLDivElement> {
	initialContent: CourseDraft | QuizDraft | ArticleDraft;
	onPublisherChange: (author?: Author) => void;
	onKeepOwnershipChange: (keepOwnership: boolean) => void;
	isArticle?: boolean;
}
export const PublisherSelector: React.FC<PublisherSelectorProps> = ({
	initialContent,
	onPublisherChange,
	onKeepOwnershipChange,
	isArticle,
	...htmlProps
}) => {
	const { result: organizations, totalElements } = useMyOrganizations();
	const currentProfile = useCurrentProfile();
	const currentPublisher = useMemo(() => initialContent.publisher ?? (currentProfile as Person), [initialContent]);
	const keepOwnership = useMemo(() => initialContent.keepOwnership, [initialContent]);

	if (!currentProfile || !totalElements || organizations.length < 1) {
		return null;
	}

	return (
		<Container {...htmlProps}>
			<Primary>
				<b>{I18n.t("course.editor.publisher.label")}</b>
			</Primary>
			<PersonSelector
				current={currentPublisher}
				items={[currentProfile, ...organizations]}
				onItemChange={onPublisherChange}
				onlyOrganizations
			/>
			{currentPublisher.type !== "USER" && (
				<RadioButton
					checkbox
					id="ownership"
					name="ownership"
					label={I18n.t("course.editor.publisher.keep_ownership")}
					checked={keepOwnership}
					onChange={() => onKeepOwnershipChange(!keepOwnership)}
				/>
			)}
		</Container>
	);
};

const Container = styled.div`
	width: 100%;
	${stack(13, "flex-start", "stretch")};
`;
